import { Controller } from "@hotwired/stimulus"
import { animatePokeballs } from "../pokeball_animation"

export default class extends Controller {
  static targets = ["hiddenCount"]

  connect() {
    this.escapeHandler = this.close.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Use event delegation for image card clicks
    this.element.addEventListener("click", this.handleClick.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
  }

  handleClick(event) {
    const target = event.target
    if (target.closest('input[type="checkbox"]') || target.closest('a') || target.closest('button')) {
      return
    }

    const card = event.target.closest("[data-image-card]")
    if (card) {
      this.openFromCard(card)
    }
  }

  openFromCard(card) {
    const imagePath = card.dataset.imagePath
    const creator = card.dataset.creator
    const publishedAt = card.dataset.publishedAt || ""
    const tweetUrl = card.dataset.tweetUrl || ""
    let publicationsData = []
    try {
      const pubsRaw = card.dataset.publications
      if (pubsRaw && pubsRaw.length > 0) {
        publicationsData = JSON.parse(pubsRaw.replace(/&quot;/g, '"'))
      }
    } catch (e) {
      // Ignore parse errors
    }

    const modal = this.element.querySelector("#image-modal")
    const modalImage = modal.querySelector("#modal-image")
    const modalContent = modal.querySelector("#modal-content")

    modalImage.src = `${imagePath}`

    let publicationsHtml = ''
    if (publicationsData.length > 0) {
      publicationsHtml = `<div class="text-green-600 text-sm">`
      publicationsData.forEach(pub => {
        publicationsHtml += `<div>Na #${this.escapeHtml(pub.channel)} ${pub.date}</div>`
      })
      publicationsHtml += `</div>`
    }

    modalContent.innerHTML = `
      <span class="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">${this.escapeHtml(creator)}</span>
      ${publishedAt ? `<span class="text-gray-500 text-sm">${publishedAt}</span>` : ''}
      ${publicationsHtml}
      ${tweetUrl ? `<a href="${this.escapeHtml(tweetUrl)}" target="_blank" class="text-pokemon-blue hover:underline text-sm">Zobacz tweet →</a>` : ''}
    `

    modal.classList.remove("hidden", "opacity-0")
    modal.classList.add("flex", "opacity-100")
    modalImage.classList.remove("scale-95")
    modalImage.classList.add("scale-100")
    document.body.style.overflow = "hidden"
  }

  close() {
    const modal = this.element.querySelector("#image-modal")
    const modalImage = modal.querySelector("#modal-image")

    modal.classList.remove("opacity-100", "flex")
    modal.classList.add("opacity-0")
    modalImage.classList.remove("scale-100")
    modalImage.classList.add("scale-95")

    setTimeout(() => {
      modal.classList.add("hidden")
      document.body.style.overflow = ""
    }, 150)
  }

  closeOnOverlay(event) {
    const modal = this.element.querySelector("#image-modal")
    if (event.target === modal) {
      this.close()
    }
  }

  hideWithAnimation(card) {
    if (card.dataset.animating) return
    card.dataset.animating = "true"
    card.style.transition = "opacity 0.18s"
    card.style.opacity = "0"
    const rect = card.getBoundingClientRect()
    animatePokeballs(rect)
    card.addEventListener('transitionend', () => this.collapseCard(card), { once: true })
  }

  collapseCard(card) {
    const height = card.getBoundingClientRect().height
    card.style.height = height + 'px'

    // Force reflow so browser registers the explicit height before animating to 0
    // eslint-disable-next-line no-unused-expressions
    card.offsetHeight

    let done = false
    const finish = () => {
      if (done) return
      done = true
      card.style.display = 'none'
      delete card.dataset.animating
    }

    card.addEventListener('transitionend', finish, { once: true })
    setTimeout(finish, 350)

    requestAnimationFrame(() => {
      card.style.height = '0'
      card.style.paddingTop = '0'
      card.style.paddingBottom = '0'
      card.style.marginTop = '0'
      card.style.marginBottom = '0'
    })
  }

  async hideImage(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const imageId = button.dataset.imageId

    try {
      const response = await fetch(`/images/${imageId}/hide`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json"
        }
      })

      if (response.ok) {
        const card = this.element.querySelector(`[data-image-card][data-image-id="${imageId}"]`)
        if (card) this.hideWithAnimation(card)
        this.dispatch("cardsRemoved")
      }
    } catch (error) {
      console.error("Error hiding image:", error)
    }
  }

  async unhideImage(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const imageId = button.dataset.imageId

    try {
      const response = await fetch(`/images/${imageId}/unhide`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json"
        }
      })

      if (response.ok) {
        const card = this.element.querySelector(`[data-image-card][data-image-id="${imageId}"]`)
        if (card) this.hideWithAnimation(card)
        this.dispatch("cardsRemoved")
      }
    } catch (error) {
      console.error("Error unhiding image:", error)
    }
  }

  async hideSelected(event) {
    event.preventDefault()
    const checkedBoxes = this.element.querySelectorAll('input[name="image_ids[]"]:checked')

    if (checkedBoxes.length === 0) return

    const imageIds = Array.from(checkedBoxes).map(cb => cb.value)

    const sortedCards = imageIds
      .map(id => {
        const card = this.element.querySelector(`[data-image-card][data-image-id="${id}"]`)
        if (!card) return null
        const rect = card.getBoundingClientRect()
        return { card, rect }
      })
      .filter(Boolean)
      .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)

    try {
      const response = await fetch("/images/hide_selected", {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ image_ids: imageIds })
      })

      if (response.ok) {
        sortedCards.forEach(({ card }, index) => {
          setTimeout(() => this.hideWithAnimation(card), index * 150)
        })
        this.dispatch("cardsRemoved")
      }
    } catch (error) {
      console.error("Error hiding selected:", error)
    }
  }

  async unhideSelected(event) {
    event.preventDefault()
    const checkedBoxes = this.element.querySelectorAll('input[name="image_ids[]"]:checked')

    if (checkedBoxes.length === 0) return

    const imageIds = Array.from(checkedBoxes).map(cb => cb.value)

    try {
      const response = await fetch("/images/unhide_selected", {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ image_ids: imageIds })
      })

      if (response.ok) {
        imageIds.forEach(id => {
          const card = this.element.querySelector(`[data-image-card][data-image-id="${id}"]`)
          if (card) this.hideWithAnimation(card)
        })
        this.dispatch("cardsRemoved")
      }
    } catch (error) {
      console.error("Error unhiding selected:", error)
    }
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
